import { useMemo, useState } from 'react'
import { AlertTriangle, Check, Loader2 } from 'lucide-react'
import {
  useGetTagScrapeConfigQuery,
  useUpdateTagScrapeConfigMutation,
  type TagRecord,
  type TagScrapeStatusRecord
} from '@axilon-apps/domains-ontology-ui-state'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '../../components/ui/alert-dialog'
import { Badge } from '../../components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select'
import { Button } from '../../components/ui/button'
import { Switch } from '../../components/ui/switch'
import { formatRelativeTime } from '../../lib/time'
import { buildCadenceOptions, formatCadence } from '../SettingsPage/connection-health/cadence'
import {
  liveConnectivityDescription,
  liveConnectivityLabel,
  type Connectivity
} from './connectivity'
import { targetModeForSourceMode } from './acquisition-source'
import { deriveHistorizationStatus, HISTORY_PROVIDER_LABEL } from './historization-status'

interface TagAcquisitionPanelProps {
  tag: TagRecord
  graphOnly?: boolean
  registrationVendor?: string
  registrationProtocol?: string
  acquisitionQuarantined?: boolean
  gatewayHistoryEnabled?: boolean
  piHistory?: boolean
  canaryHistory?: boolean
  status?: TagScrapeStatusRecord
  connectivity?: Connectivity
  lastReadingAt?: string
  onChanged?: () => void
  onRegister?: () => void
  onChangeSource?: () => void
  canConfigureHistory?: boolean
}

const QUARANTINE_LABELS: Record<string, string> = {
  opcda_unsupported: 'OPC-DA history collection is not supported',
  unrecognized_source_type: 'The history source type is not recognized',
  ignition_relay_unparseable: 'The Ignition relay address could not be parsed',
  mtconnect_non_mtc_path: 'The tag path is not a valid MTConnect address',
  mtconnect_unparseable: 'The MTConnect address could not be parsed',
  database_non_db_path: 'The tag path is not a valid database address',
  database_unparseable: 'The database address could not be parsed',
  quarantined_registration: 'The history registration needs review'
}

function humanizeQuarantineRule(rule: string | null): string {
  if (!rule) return QUARANTINE_LABELS.quarantined_registration
  if (QUARANTINE_LABELS[rule]) return QUARANTINE_LABELS[rule]
  if (rule.endsWith('_illegal')) return 'The selected history mode is not supported'
  return rule
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

function sourceTypeLabel(sourceType: TagRecord['sourceType']): string {
  switch (sourceType) {
    case 'ignition':
      return 'Ignition WebDev'
    case 'opcua':
      return 'OPC UA'
    case 'opcda':
      return 'OPC-DA'
    case 'mtconnect':
      return 'MTConnect'
    case 'pi':
      return 'PI historian'
    case 'canary':
      return 'Canary historian'
    case 'database':
      return 'Database'
    default:
      return 'Unknown'
  }
}

function credentialProviderLabel(provider: string): string {
  switch (provider) {
    case 'webdev':
      return 'Ignition WebDev'
    case 'opc':
      return 'OPC endpoint'
    case 'mtconnect':
      return 'MTConnect Agent'
    case 'pi':
      return 'PI Web API endpoint'
    case 'canary':
      return 'Canary Views endpoint'
    case 'database':
      return 'Database connection'
    default:
      return 'History source'
  }
}

const AcquisitionRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex items-start justify-between gap-3 border-b border-dashed border-line py-2 last:border-b-0">
    <span className="text-11 font-semibold uppercase tracking-wider text-ink-3">{label}</span>
    <div className="min-w-0 text-right text-13 text-ink">{children}</div>
  </div>
)

export default function TagAcquisitionPanel({
  tag,
  graphOnly = false,
  registrationVendor,
  registrationProtocol,
  acquisitionQuarantined = false,
  gatewayHistoryEnabled = false,
  piHistory = false,
  canaryHistory = false,
  status,
  connectivity,
  lastReadingAt,
  onChanged,
  onRegister,
  onChangeSource,
  canConfigureHistory = true
}: TagAcquisitionPanelProps) {
  const configQuery = useGetTagScrapeConfigQuery(
    { name: tag.name },
    { skip: graphOnly || tag.id === '' || !canConfigureHistory }
  )
  const [updateConfig, updateState] = useUpdateTagScrapeConfigMutation()
  const [pendingEnabled, setPendingEnabled] = useState<boolean | null>(null)
  const [localError, setLocalError] = useState<string | null>(null)

  const config = configQuery.data
  const quarantined = acquisitionQuarantined || config?.acquisitionQuarantined === true
  const currentEnabled = config?.enabled ?? tag.state !== 'disabled'
  const cadenceMs = config?.frequencyMs ?? tag.frequencyMs
  const cadenceOptions = useMemo(() => buildCadenceOptions(cadenceMs), [cadenceMs])
  const runtimeLabel = !currentEnabled
    ? 'Disabled'
    : connectivity
      ? liveConnectivityLabel(connectivity)
      : (status?.state ?? 'Waiting for status')
  const runtimeClass =
    currentEnabled && connectivity === 'error'
      ? 'font-semibold text-st-down'
      : currentEnabled &&
          (connectivity === 'stale' ||
            connectivity === 'reconnecting' ||
            connectivity === 'bad_quality')
        ? 'font-semibold text-st-warn'
        : undefined
  const runtimeDescription = currentEnabled
    ? connectivity
      ? liveConnectivityDescription(connectivity)
      : 'Runtime status has not loaded yet.'
    : 'History is disabled; no new values are being collected.'
  const historization = deriveHistorizationStatus({
    enabled: currentEnabled,
    acquisitionQuarantined: quarantined,
    registrationProtocol: registrationProtocol || config?.protocol,
    sourceType: tag.sourceType,
    piHistory,
    canaryHistory,
    gatewayHistoryEnabled
  })
  const changeSourceDisabledReason = !tag.sourceUrl.trim()
    ? 'This registration does not have a source URL.'
    : targetModeForSourceMode(tag.scrapeMode) === null
      ? 'Change source supports WebDev poll and OPC subscribe registrations.'
      : undefined

  if (graphOnly) {
    return (
      <div
        className="rounded-md border border-dashed border-line bg-bg-sunk/50 p-4"
        data-testid="tag-acquisition-graph-only"
      >
        <div className="text-13 font-semibold text-ink">Not registered for history</div>
        <p className="m-0 mt-1.5 text-12 leading-relaxed text-ink-3">
          This ontology tag has no history registration. Collection settings become available after
          it is connected to a data source.
        </p>
        {onRegister ? (
          <Button
            size="sm"
            className="mt-3 h-7 text-11"
            onClick={onRegister}
            data-testid="tag-acquisition-register"
          >
            Register this tag
          </Button>
        ) : null}
      </div>
    )
  }

  if (canConfigureHistory && configQuery.isLoading && !config) {
    return (
      <div className="flex items-center gap-2 py-6 text-12 text-ink-3">
        <Loader2 className="size-4 animate-spin" aria-hidden />
        Loading history settings…
      </div>
    )
  }

  if (canConfigureHistory && configQuery.error && !config) {
    return (
      <div className="rounded-md bg-st-down-bg p-3 text-12 text-st-down">
        History settings are unavailable: {configQuery.error}
      </div>
    )
  }

  const saveCadence = async (value: string) => {
    const frequencyMs = Number(value)
    if (!Number.isFinite(frequencyMs) || frequencyMs <= 0 || quarantined) return
    setLocalError(null)
    try {
      await updateConfig({ name: tag.name, frequencyMs })
      onChanged?.()
    } catch (error) {
      setLocalError(error instanceof Error ? error.message : String(error))
    }
  }

  const confirmCollectionChange = async () => {
    if (pendingEnabled === null || quarantined) return
    const enabled = pendingEnabled
    setLocalError(null)
    try {
      await updateConfig({ name: tag.name, enabled })
      setPendingEnabled(null)
      onChanged?.()
    } catch (error) {
      setLocalError(error instanceof Error ? error.message : String(error))
    }
  }

  const credential = config?.credential
  const credentialValue = credential
    ? credential.anonymousAccess === true
      ? 'Anonymous access'
      : credential.hasPassword
        ? 'Password configured'
        : 'No password configured'
    : 'No credential posture reported'

  return (
    <>
      <div className="space-y-3" data-testid="tag-acquisition-tab">
        {quarantined ? (
          <div
            className="flex items-start gap-2 rounded-md bg-st-warn-bg p-3 text-st-warn"
            data-testid="tag-acquisition-quarantine"
          >
            <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
            <div>
              <div className="text-12 font-semibold">History changes are blocked</div>
              <div className="mt-0.5 text-12">
                {humanizeQuarantineRule(config?.quarantineRule ?? null)}
              </div>
              {config?.quarantineNote ? (
                <div className="mt-1 text-11 opacity-80">{config.quarantineNote}</div>
              ) : null}
            </div>
          </div>
        ) : null}

        <section className="rounded-md border border-line bg-bg-body p-3">
          <h3 className="m-0 mb-1 text-11 font-semibold uppercase tracking-wider text-ink-3">
            History source
          </h3>
          <AcquisitionRow label="Registration">
            <span className="font-mono">{tag.id}</span>
          </AcquisitionRow>
          <AcquisitionRow label="Source">
            {registrationVendor || config?.vendor || sourceTypeLabel(tag.sourceType)}
          </AcquisitionRow>
          <AcquisitionRow label="Protocol">
            {registrationProtocol || config?.protocol || sourceTypeLabel(tag.sourceType)}
          </AcquisitionRow>
          <AcquisitionRow label="Transport">
            <span className="capitalize">{tag.scrapeMode}</span>
            {config?.relayFallbackEnabled ? ' with Ignition relay fallback' : ''}
          </AcquisitionRow>
          {config?.relayGatewayUrl ? (
            <AcquisitionRow label="Relay">
              <span className="block truncate font-mono" title={config.relayGatewayUrl}>
                {config.relayGatewayUrl}
              </span>
              {config.relayWebDevProject || config.relayOpcServer ? (
                <span className="block text-11 text-ink-3">
                  {[config.relayWebDevProject, config.relayOpcServer].filter(Boolean).join(' · ')}
                </span>
              ) : null}
            </AcquisitionRow>
          ) : null}
          <AcquisitionRow label="Credentials">
            <span className="block">{credentialValue}</span>
            {credential ? (
              <span className="block text-11 text-ink-3">
                {credentialProviderLabel(credential.provider)}
              </span>
            ) : null}
          </AcquisitionRow>
          <AcquisitionRow label="Runtime">
            <span
              className={['capitalize', runtimeClass].filter(Boolean).join(' ')}
              title={runtimeDescription}
              data-testid="tag-acquisition-runtime-state"
            >
              {runtimeLabel}
            </span>
            <span className="block text-11 text-ink-3">
              Last reading{' '}
              {formatRelativeTime(lastReadingAt ?? status?.lastScrapeTime, { fallback: '—' })}
            </span>
          </AcquisitionRow>
          {onChangeSource ? (
            <div className="flex justify-end border-t border-line pt-2">
              <span title={changeSourceDisabledReason}>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-11"
                  onClick={onChangeSource}
                  disabled={Boolean(changeSourceDisabledReason)}
                  data-testid="tag-acquisition-change-source"
                  data-disabled-reason={changeSourceDisabledReason}
                >
                  Change source
                </Button>
              </span>
            </div>
          ) : null}
        </section>

        <section className="rounded-md border border-line bg-bg-body p-3">
          <h3 className="m-0 mb-1 text-11 font-semibold uppercase tracking-wider text-ink-3">
            Collection
          </h3>
          <AcquisitionRow label="History">
            <div className="flex items-center justify-end gap-2">
              <span
                className={currentEnabled ? undefined : 'text-ink-3'}
                data-testid="tag-acquisition-enabled-state"
              >
                {currentEnabled ? 'Enabled' : 'Disabled'}
              </span>
              <Switch
                checked={currentEnabled}
                onCheckedChange={(checked) => setPendingEnabled(checked)}
                disabled={!canConfigureHistory || quarantined || updateState.isLoading}
                aria-label="History enabled"
                data-testid="tag-acquisition-enabled-switch"
              />
            </div>
          </AcquisitionRow>
          <AcquisitionRow label="Cadence">
            <Select
              value={String(cadenceMs)}
              onValueChange={(value) => void saveCadence(value)}
              disabled={!canConfigureHistory || quarantined || updateState.isLoading}
            >
              <SelectTrigger
                size="sm"
                className="ml-auto min-w-37.5 text-12"
                data-testid="tag-acquisition-cadence"
              >
                <SelectValue>{formatCadence(cadenceMs)}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {cadenceOptions.map((option) => (
                  <SelectItem key={option.value} value={String(option.value)}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </AcquisitionRow>
        </section>

        <section
          className="rounded-md border border-line bg-bg-body p-3"
          data-testid="tag-history-providers"
          data-historization={historization.kind}
        >
          <h3 className="m-0 mb-2 text-11 font-semibold uppercase tracking-wider text-ink-3">
            History providers
          </h3>
          {historization.providers.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {historization.providers.map((provider) => (
                <Badge key={provider} variant="outline" className="gap-1 text-11">
                  <Check className="size-3" aria-hidden />
                  {HISTORY_PROVIDER_LABEL[provider]}
                  {provider === 'internal' ? null : ' (external)'}
                </Badge>
              ))}
            </div>
          ) : (
            <div className="text-12 font-semibold text-st-warn">Not historized</div>
          )}
          <p
            className="m-0 mt-2 text-11 leading-relaxed text-ink-3"
            data-testid="tag-history-providers-reason"
          >
            {historization.reason}
          </p>
        </section>

        {status?.lastError ? (
          <div className="rounded-md bg-st-down-bg p-3 text-12 text-st-down">
            {status.lastError}
          </div>
        ) : null}
        {localError ? (
          <div className="rounded-md bg-st-down-bg p-3 text-12 text-st-down">{localError}</div>
        ) : null}
      </div>

      <AlertDialog
        open={pendingEnabled !== null}
        onOpenChange={(open) => {
          if (!open && !updateState.isLoading) setPendingEnabled(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingEnabled ? 'Enable history?' : 'Disable history?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingEnabled
                ? 'Scheduled collection and new history will resume for this tag.'
                : 'Scheduled collection and new history will stop. Live reads remain available.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={updateState.isLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault()
                void confirmCollectionChange()
              }}
              disabled={updateState.isLoading}
              variant={pendingEnabled ? 'default' : 'destructive'}
              data-testid="tag-acquisition-confirm"
            >
              {updateState.isLoading ? (
                <>
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                  Updating…
                </>
              ) : pendingEnabled ? (
                'Enable history'
              ) : (
                'Disable history'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
